import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import EmojiRemoveIcon from '@atlaskit/icon/core/emoji-remove';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { makeKeymap, tooltip } from '@atlaskit/editor-common/keymaps';

import { TabColorMenuItem } from '../tab-color/TabColorMenuItem.tsx';
import { TabColorMenuTrigger } from '../tab-color/TabColorMenuTrigger.tsx';
import type { TabOrientation } from '../nativeTabsTypes.ts';
import type { TabColorKey } from '../tab-color/tabColorDefinitions.ts';

const moveTabLeftKeymap = makeKeymap(
	'Move tab left',
	'Ctrl-Alt-ArrowLeft',
	'Cmd-Ctrl-Alt-ArrowLeft',
);
const moveTabRightKeymap = makeKeymap(
	'Move tab right',
	'Ctrl-Alt-ArrowRight',
	'Cmd-Ctrl-Alt-ArrowRight',
);
const moveTabLeftShortcut = tooltip(moveTabLeftKeymap);
const moveTabRightShortcut = tooltip(moveTabRightKeymap);
const moveTabUpShortcut = tooltip(
	makeKeymap('Move tab up', 'Ctrl-Alt-ArrowUp', 'Cmd-Ctrl-Alt-ArrowUp'),
);
const moveTabDownShortcut = tooltip(
	makeKeymap('Move tab down', 'Ctrl-Alt-ArrowDown', 'Cmd-Ctrl-Alt-ArrowDown'),
);

const styles = cssMap({
	shortcut: {
		backgroundColor: token('color.background.neutral'),
		color: token('color.text.subtle'),
		borderRadius: token('radius.small'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		alignSelf: 'flex-end',
		font: token('font.body.small'),
	},
});

const i18n = defineMessages({
	tabOptions: {
		id: 'native-tabs.tab-pill-menu.tab-options.non-final',
		defaultMessage: 'Tab options',
		description: 'Accessible label for the ellipsis icon button that opens the tab options menu.',
	},
	addEmoji: {
		id: 'native-tabs.tab-pill-menu.add-emoji.non-final',
		defaultMessage: 'Add emoji',
		description: 'Dropdown menu item label to add an emoji to the tab.',
	},
	removeEmoji: {
		id: 'native-tabs.tab-pill-menu.remove-emoji.non-final',
		defaultMessage: 'Remove emoji',
		description: 'Dropdown menu item label to remove the emoji from the tab.',
	},
	copyLink: {
		id: 'native-tabs.tab-pill-menu.copy-link.non-final',
		defaultMessage: 'Copy link to tab',
		description: 'Dropdown menu item label to copy a direct link to the tab.',
	},
	deleteTab: {
		id: 'native-tabs.tab-pill-menu.delete-tab.non-final',
		defaultMessage: 'Delete',
		description: 'Dropdown menu item label to delete the tab.',
	},
	moveTabLeft: {
		id: 'native-tabs.tab-pill-menu.move-tab-left.non-final',
		defaultMessage: 'Move tab left',
		description: 'Dropdown menu item label to move the active tab one position to the left.',
	},
	moveTabRight: {
		id: 'native-tabs.tab-pill-menu.move-tab-right.non-final',
		defaultMessage: 'Move tab right',
		description: 'Dropdown menu item label to move the active tab one position to the right.',
	},
	moveTabUp: {
		id: 'native-tabs.tab-pill-menu.move-tab-up.non-final',
		defaultMessage: 'Move tab up',
		description: 'Dropdown menu item label to move the active vertical tab one position up.',
	},
	moveTabDown: {
		id: 'native-tabs.tab-pill-menu.move-tab-down.non-final',
		defaultMessage: 'Move tab down',
		description: 'Dropdown menu item label to move the active vertical tab one position down.',
	},
});

export type TabPillMenuProps = {
	/** Whether the active tab already has an emoji set. */
	hasEmoji: boolean;
	/** Called when the user clicks "Add emoji". */
	onEmojiPickerOpen?: () => void;
	/** Called when the user clicks "Remove emoji". */
	onRemoveEmoji?: () => void;
	/** Whether the emoji picker popup is currently open. */
	isEmojiPickerOpen: boolean;
	/** Called when the emoji picker popup should close. */
	onEmojiPickerClose?: () => void;
	/** The React node representing the emoji picker content. */
	emojiPickerContent: ReactNode;
	/** Called when the user clicks "Move tab left". */
	onMoveTabPrevious?: () => void;
	/** Called when the user clicks "Move tab right". */
	onMoveTabNext?: () => void;
	/** Called when the user clicks "Copy link to tab". */
	onCopyLink?: () => void;
	/** Called when the user clicks "Delete". */
	onDeleteTab?: () => void;
	/** Whether the "Delete" item should be disabled. */
	isDeleteDisabled: boolean;
	/** Called when the user hovers or focuses the "Delete" item. */
	onDeletePreviewStart?: () => void;
	/** Called when the user stops hovering or focusing the "Delete" item. */
	onDeletePreviewEnd?: () => void;
	/** Whether the tab is currently previewing a destructive delete. */
	isDeletePreviewed?: boolean;
	/** Whether the active tab is the first tab (disables "Move tab left"). */
	isFirst: boolean;
	/** Whether the active tab is the last tab (disables "Move tab right"). */
	isLast: boolean;
	/** Stable tab id used to derive unique DOM ids (e.g. for Toggle label association). */
	tabId: string;
	/** The current color of the tab, if any. */
	currentColor?: TabColorKey;
	/** Called with the selected color key, or `undefined` to clear the color. */
	onColorChange?: (color: TabColorKey | undefined) => void;
	/** Whether the picker applies the selected color to every tab. */
	applyToAll?: boolean;
	/** Called when the picker changes its apply-to-all setting. */
	onApplyToAllChange?: (value: boolean) => void;
	/** Used to close the menu when the tab bar scrolls. */
	scrollContainerRef?: React.RefObject<HTMLDivElement>;
	/** Tab navigation orientation. */
	orientation?: TabOrientation;
};

/**
 * `TabPillMenu` — ellipsis trigger + dropdown for an active tab pill.
 *
 * When `isEmojiPickerOpen` is true, the dropdown is replaced by the emoji picker
 * rendered via a second `DropdownMenu` anchored to the same trigger button.
 *
 * Must be rendered as a sibling of `TabPill`, never nested inside it, to avoid
 * placing an interactive element inside a `role="tab"` (ARIA violation).
 */
export const TabPillMenu: {
	({
		hasEmoji,
		onEmojiPickerOpen,
		onRemoveEmoji,
		isEmojiPickerOpen,
		onEmojiPickerClose,
		emojiPickerContent,
		onMoveTabPrevious,
		onMoveTabNext,
		onCopyLink,
		onDeleteTab,
		isDeleteDisabled,
		onDeletePreviewStart,
		onDeletePreviewEnd,
		isDeletePreviewed,
		isFirst,
		isLast,
		currentColor,
		onColorChange,
		applyToAll,
		onApplyToAllChange,
		scrollContainerRef,
		orientation,
		tabId,
	}: TabPillMenuProps): React.JSX.Element;
	displayName: string;
} = ({
	hasEmoji,
	onEmojiPickerOpen,
	onRemoveEmoji,
	isEmojiPickerOpen,
	onEmojiPickerClose,
	emojiPickerContent,
	onMoveTabPrevious,
	onMoveTabNext,
	onCopyLink,
	onDeleteTab,
	isDeleteDisabled,
	onDeletePreviewStart,
	onDeletePreviewEnd,
	isDeletePreviewed = false,
	isFirst,
	isLast,
	currentColor,
	onColorChange,
	applyToAll,
	onApplyToAllChange,
	scrollContainerRef,
	orientation = 'horizontal',
}: TabPillMenuProps): React.JSX.Element => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const closeMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	useEffect(() => {
		const el = scrollContainerRef?.current;
		if (!el) return;
		el.addEventListener('scroll', closeMenu, { passive: true });
		return () => el.removeEventListener('scroll', closeMenu);
	}, [closeMenu, scrollContainerRef]);

	const { formatMessage } = useIntl();
	const tabOptionsLabel = formatMessage(i18n.tabOptions);
	const handleDeletePreviewStart = useCallback(() => {
		if (!isDeleteDisabled) {
			onDeletePreviewStart?.();
		}
	}, [isDeleteDisabled, onDeletePreviewStart]);
	const handleDeletePreviewEnd = useCallback(() => {
		onDeletePreviewEnd?.();
	}, [onDeletePreviewEnd]);

	return (
		<DropdownMenu
			isOpen={isMenuOpen || isEmojiPickerOpen}
			placement={orientation === 'vertical' ? 'left-start' : 'bottom-end'}
			spacing="compact"
			onOpenChange={({ isOpen }) => {
				if (!isOpen) {
					if (isEmojiPickerOpen) {
						onEmojiPickerClose?.();
					}
					setIsMenuOpen(false);
					handleDeletePreviewEnd();
				} else if (!isEmojiPickerOpen) {
					setIsMenuOpen(true);
				}
			}}
			trigger={({ triggerRef, ...triggerProps }) => (
				<TabColorMenuTrigger
					{...triggerProps}
					ref={(el) => {
						if (typeof triggerRef === 'function') triggerRef(el);
						else if (triggerRef)
							(triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
					}}
					label={tabOptionsLabel}
					currentColor={currentColor}
					isDeletePreviewed={isDeletePreviewed}
					orientation={orientation}
				/>
			)}
		>
			{isEmojiPickerOpen ? (
				emojiPickerContent
			) : (
				<>
					{/* Group 1: Color controls */}
					<TabColorMenuItem
						currentColor={currentColor}
						onColorChange={onColorChange}
						applyToAll={applyToAll}
						onApplyToAllChange={onApplyToAllChange}
						isParentMenuOpen={isMenuOpen}
					/>
					{/* Group 2: Emoji + Copy link */}
					<DropdownItemGroup hasSeparator>
						{hasEmoji ? (
							<DropdownItem
								elemBefore={
									<EmojiRemoveIcon label={formatMessage(i18n.removeEmoji)} size="small" />
								}
								onClick={onRemoveEmoji}
							>
								<Text color="color.text.subtle">
									<FormattedMessage {...i18n.removeEmoji} />
								</Text>
							</DropdownItem>
						) : (
							<DropdownItem
								elemBefore={<EmojiAddIcon label={formatMessage(i18n.addEmoji)} size="small" />}
								onClick={onEmojiPickerOpen}
							>
								<Text color="color.text.subtle">
									<FormattedMessage {...i18n.addEmoji} />
								</Text>
							</DropdownItem>
						)}
						<DropdownItem
							elemBefore={<LinkIcon label={formatMessage(i18n.copyLink)} size="small" />}
							onClick={() => {
								closeMenu();
								onCopyLink?.();
							}}
						>
							<Text color="color.text.subtle">
								<FormattedMessage {...i18n.copyLink} />
							</Text>
						</DropdownItem>
					</DropdownItemGroup>
					{/* Group 3: Move controls */}
					<DropdownItemGroup hasSeparator>
						<DropdownItem
							elemBefore={
								orientation === 'vertical' ? (
									<ArrowUpIcon label="" color="currentColor" size="small" />
								) : (
									<ArrowLeftIcon label="" color="currentColor" size="small" />
								)
							}
							elemAfter={
								<Box xcss={styles.shortcut}>
									{orientation === 'vertical' ? moveTabUpShortcut : moveTabLeftShortcut}
								</Box>
							}
							onClick={onMoveTabPrevious}
							isDisabled={isFirst}
						>
							<Text color="color.text.subtle">
								<FormattedMessage
									{...(orientation === 'vertical' ? i18n.moveTabUp : i18n.moveTabLeft)}
								/>
							</Text>
						</DropdownItem>
						<DropdownItem
							elemBefore={
								orientation === 'vertical' ? (
									<ArrowDownIcon label="" color="currentColor" size="small" />
								) : (
									<ArrowRightIcon label="" color="currentColor" size="small" />
								)
							}
							elemAfter={
								<Box xcss={styles.shortcut}>
									{orientation === 'vertical' ? moveTabDownShortcut : moveTabRightShortcut}
								</Box>
							}
							onClick={onMoveTabNext}
							isDisabled={isLast}
						>
							<Text color="color.text.subtle">
								<FormattedMessage
									{...(orientation === 'vertical' ? i18n.moveTabDown : i18n.moveTabRight)}
								/>
							</Text>
						</DropdownItem>
					</DropdownItemGroup>
					{/* Group 4: Delete */}
					<DropdownItemGroup hasSeparator>
						<div
							role="presentation"
							onMouseEnter={handleDeletePreviewStart}
							onMouseLeave={handleDeletePreviewEnd}
							onFocus={handleDeletePreviewStart}
							onBlur={handleDeletePreviewEnd}
						>
							<DropdownItem
								elemBefore={
									<DeleteIcon
										label={formatMessage(i18n.deleteTab)}
										color={
											isDeleteDisabled ? token('color.text.disabled') : token('color.text.danger')
										}
										size="small"
									/>
								}
								onClick={() => {
									handleDeletePreviewEnd();
									onDeleteTab?.();
								}}
								isDisabled={isDeleteDisabled}
							>
								<Text color={isDeleteDisabled ? 'color.text.disabled' : 'color.text.danger'}>
									<FormattedMessage {...i18n.deleteTab} />
								</Text>
							</DropdownItem>
						</div>
					</DropdownItemGroup>
				</>
			)}
		</DropdownMenu>
	);
};

TabPillMenu.displayName = 'TabPillMenu';
